import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Alert,
  Modal,
  FlatList,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Switch,
  Image,
} from '../components/rn';
import { useWindowDimensions } from 'react-native';
import ActivityIndicator from '../components/LoadingIndicator';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { profileFieldStyle, profilePalette } from '../theme/profileSection';
import ProfileSection from '../components/ProfileSection';
import SectionActionRow from '../components/SectionActionRow';
import ChatBackupSheet from '../components/ChatBackupSheet';
import SignInMethodsScreen from './SignInMethodsScreen';
import { useTheme, useThemeMode, ThemeMode } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { normalizeInputDigits } from '../i18n/inputDigits';
import { DISPLAY_LANGUAGES } from '../i18n';
import { api, ApiError, BASE_URL, BlockedUser, Profession, Education, UserProfession, ProfileEmployer, CallException, MessageException, type ProfilePreferences } from '../services/api';
import { callExceptionStatusKey, messageExceptionStatusKey } from '../calls/callExceptions';
import { iscoMajorOf } from '../data/isco';
import { flagForLang } from '../data/lang-flags';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import { mediaUrl } from '../services/api';
import { localizedCountry } from '../data/country-names';
import { CURRENCIES, formatMoney, formatMoneyAmount, symbolFor } from '../data/currencies';
import NotificationBellButton from '../components/NotificationBellButton';
import GlassControlButton from '../components/GlassControlButton';
import ContactExceptionsModal from '../components/ContactExceptionsModal';
import { InfoButton, InfoText, LabelWithInfo, useInfoSet } from '../components/InfoHint';
import { storage } from '../services/storage';
import { uppercaseForDisplayLanguage } from '../utils/locale';
import { deliverAccountExport } from '../account/accountExport';

type Props = {
  token: string;
  myId: number;
  onWallet: () => void;
  onNotifications: () => void;
  onCalendarConnections: () => void;
  onInstagramConnections: () => void;
  onNotificationCenter: () => void;
  exceptionReturnContext?: ExceptionListContext | null;
  onExceptionReturnHandled?: () => void;
  onOpenUserProfile: (userId: number, context: ExceptionListContext) => void;
  onLogout: () => void;
  /** 7.6 — aynı bileşen iki ekran sunar: herkese açık profil ve ayarlar.
   *  Durum ve para/izin mantığı tek yerde kaldığı için taşıma riski doğmaz. */
  variant?: 'profile' | 'settings';
  onSettings?: () => void;
  onBack?: () => void;
};

export type ExceptionListContext = {
  kind: 'call' | 'message';
  query: string;
};

type Profile = {
  id: number;
  name: string;
  username: string | null;
  surname: string | null;
  photo_url: string | null;
  price_message: number;
  price_voice: number;
  price_call: number;
  price_call_scheduled: number;
  price_call_video: number;
  price_time: number;
  currency: string;
  bio: string | null;
  show_read_receipts: boolean;
  who_can_message: string;
  keep_archived: boolean;
  deletion_scheduled_at: string | null;
  education: Education[];
  professions: UserProfession[];
  employer: ProfileEmployer | null;
};

type University = { id: number; name: string; country: string; country_code: string | null };
type PriceKind = 'message' | 'voice' | 'call' | 'videoCall' | 'scheduledCall' | 'time';

// 1.3.6–1.3.7 / D-657, D-659 — web sayfasını kimler görebilir. Sunucuda iki tercih
// var; ekran tek bir üçlü seçim gösterir. Sayfa kapalıysa "Kimse" — arama tercihi
// o zaman anlamsızdır. Eski sunucu alanları döndürmezse varsayılan Herkes (D-651).
type WebVisibility = 'everyone' | 'link' | 'nobody';
function webVisibilityOf(p: Pick<ProfilePreferences, 'web_profile_visible' | 'search_visible'>): WebVisibility {
  if (p.web_profile_visible === false) return 'nobody';
  return p.search_visible === false ? 'link' : 'everyone';
}
const SHORT_PRICE_LABELS = {
  voice: 'profile.priceVoiceShort',
  call: 'profile.priceCallShort',
  videoCall: 'profile.priceVideoCallShort',
  scheduledCall: 'profile.priceScheduledCallShort',
  time: 'profile.priceTimeShort',
} as const;

// #34 — backend'le aynı kural (users.ts:validateUsername)
const USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/;

// ISCED (UNESCO) eğitim seviyeleri — backend'le aynı liste (users.ts:ISCED_LEVELS)
const ISCED_LEVELS = [5, 6, 7, 8] as const;

export default function OwnProfileScreen({ token, myId, onWallet, onNotifications, onCalendarConnections, onInstagramConnections, onNotificationCenter, exceptionReturnContext = null, onExceptionReturnHandled, onOpenUserProfile, onLogout, variant = 'profile', onSettings, onBack }: Props) {
  const insets = useSafeAreaInsets();
  const { width, fontScale } = useWindowDimensions();
  // 7.7 — D-504'te ölçülen kusur: en büyük erişilebilirlik boyutlarında
  // `etiket + tutar + chevron` tek satıra sığmıyor ve React Native kelimenin
  // İÇİNDE bölüyor ("Mesaj" → "Mesa" / "j"). Üçünden biri feda edilemez: tutar
  // kırpılamaz, chevron satırın dokunulabilir olduğunu söyler, etiket ücretin
  // adıdır. Bu yüzden tek satırı zorlamak yerine iOS'un kendi yaptığı şey
  // yapılıyor — eşiği geçince etiket değerin ÜSTÜNE alınıyor.
  //
  // Eşik ve ölçüm biçimi CallHistoryScreen'den (D-348) alındı: aynı kusurun
  // aynı ürün içinde iki farklı sayıyla çözülmesi sapma üretirdi.
  const stacked = (width - insets.left - insets.right) / fontScale < 320;
  const colors = useTheme();
  // 7.14 / D-654 — açıklamalar ⓘ arkasında; ekran her açıldığında kapalı.
  const info = useInfoSet();
  const { mode, setMode } = useThemeMode();
  const { lang, setLang, t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [profileInfoModal, setProfileInfoModal] = useState(false);
  const [backupSheet, setBackupSheet] = useState(false);
  const [photoModal, setPhotoModal] = useState(false);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [surnameInput, setSurnameInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameCheck, setUsernameCheck] =
    useState<'idle' | 'invalid' | 'checking' | 'available' | 'taken'>('idle');
  const [priceEditor, setPriceEditor] = useState<{ kind: PriceKind; label: string } | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [employerUsernameInput, setEmployerUsernameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showReadReceipts, setShowReadReceipts] = useState(true);
  const [savingReadReceipts, setSavingReadReceipts] = useState(false);
  // 9.20.4 / D-060 — "yazıyor" bilgisi. Görüldü bilgisinin aksine karşılıklı
  // değil: kapatan yalnız kendi yazışını gizler, başkalarınınkini görmeye devam eder.
  const [showTyping, setShowTyping] = useState(true);
  const [savingShowTyping, setSavingShowTyping] = useState(false);
  const [whoCanMessage, setWhoCanMessage] = useState('everyone');
  const [savingWhoCanMessage, setSavingWhoCanMessage] = useState(false);
  const [whoCanCall, setWhoCanCall] = useState('everyone');
  const [savingWhoCanCall, setSavingWhoCanCall] = useState(false);
  const [keepArchived, setKeepArchived] = useState(false);
  // 1.3.6–1.3.7 / D-657, D-659 — web sayfasını kimler görebilir. İki tercihten
  // türüyor; varsayılan Herkes (D-651).
  const [webVisibility, setWebVisibility] = useState<WebVisibility>('everyone');
  const [savingWebVisibility, setSavingWebVisibility] = useState(false);
  // 9.32 / D-655 — link görünümü tercihi; bu cihazda, bu hesap için.
  const [linkViewDefault, setLinkViewDefault] = useState<'compact' | 'plain' | 'card'>('compact');
  const [linkViewAsk, setLinkViewAsk] = useState(true);
  useEffect(() => {
    let active = true;
    storage.getLinkViewDefault(myId).then((view) => { if (active) setLinkViewDefault(view); }).catch(() => {});
    storage.getLinkViewAsk(myId).then((ask) => { if (active) setLinkViewAsk(ask); }).catch(() => {});
    return () => { active = false; };
  }, [myId]);
  function changeLinkViewDefault(view: 'compact' | 'plain' | 'card') {
    setLinkViewDefault(view);
    storage.setLinkViewDefault(myId, view).catch(() => {});
  }
  function changeLinkViewAsk(ask: boolean) {
    setLinkViewAsk(ask);
    storage.setLinkViewAsk(myId, ask).catch(() => {});
  }
  const [savingKeepArchived, setSavingKeepArchived] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [addEduModal, setAddEduModal] = useState(false);
  const [addEduStep, setAddEduStep] = useState<'university' | 'level'>('university');
  const [addEduQuery, setAddEduQuery] = useState('');
  const [addEduResults, setAddEduResults] = useState<University[]>([]);
  const [addEduLoading, setAddEduLoading] = useState(false);
  const [addEduUniversity, setAddEduUniversity] = useState<University | null>(null);
  const [addEduSaving, setAddEduSaving] = useState(false);
  const [addProfModal, setAddProfModal] = useState(false);
  const [addProfQuery, setAddProfQuery] = useState('');
  const [addProfResults, setAddProfResults] = useState<Profession[]>([]);
  const [addProfLoading, setAddProfLoading] = useState(false);
  const [addProfSaving, setAddProfSaving] = useState(false);
  const [langModal, setLangModal] = useState(false);
  const [langQuery, setLangQuery] = useState('');
  const [currencyModal, setCurrencyModal] = useState(false);
  const [usernameModal, setUsernameModal] = useState(false);
  const [blockedModal, setBlockedModal] = useState(false);
  const [accountModal, setAccountModal] = useState(false);
  const [signInMethodsModal, setSignInMethodsModal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  // 9.4.7.5 — kişi bazlı arama istisnaları (D-227); "Engellenenler"in kardeşi.
  const [callExceptionsModal, setCallExceptionsModal] = useState(false);
  const [callExceptions, setCallExceptions] = useState<CallException[]>([]);
  const [callExceptionsLoading, setCallExceptionsLoading] = useState(false);
  const [callExceptionsLoadingMore, setCallExceptionsLoadingMore] = useState(false);
  const [callExceptionsFailed, setCallExceptionsFailed] = useState(false);
  const [callExceptionsLoadMoreFailed, setCallExceptionsLoadMoreFailed] = useState(false);
  const [callExceptionsCursor, setCallExceptionsCursor] = useState<string | null>(null);
  const [callExceptionsQuery, setCallExceptionsQuery] = useState('');
  const [messageExceptionsModal, setMessageExceptionsModal] = useState(false);
  const [messageExceptions, setMessageExceptions] = useState<MessageException[]>([]);
  const [messageExceptionsLoading, setMessageExceptionsLoading] = useState(false);
  const [messageExceptionsLoadingMore, setMessageExceptionsLoadingMore] = useState(false);
  const [messageExceptionsFailed, setMessageExceptionsFailed] = useState(false);
  const [messageExceptionsLoadMoreFailed, setMessageExceptionsLoadMoreFailed] = useState(false);
  const [messageExceptionsCursor, setMessageExceptionsCursor] = useState<string | null>(null);
  const [messageExceptionsQuery, setMessageExceptionsQuery] = useState('');
  const callExceptionsRequestRef = useRef(0);
  const messageExceptionsRequestRef = useRef(0);
  const [blockedLoading, setBlockedLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    Promise.all([
      api.getOwnUser(token, myId, lang),
      api.getProfilePreferences(token),
    ])
      .then(([p, preferences]) => {
        setProfile({
          ...p,
          ...preferences,
          price_call_scheduled: Number(p.price_call_scheduled ?? p.price_call),
          // 9.4.14 — sunucu bu alanı vermiyorsa (eski sürüm) sesli arama
          // fiyatına düşer; ekranda boş bir fiyat göstermek yanlış olurdu.
          price_call_video: Number(p.price_call_video ?? p.price_call),
          deletion_scheduled_at: p.deletion_scheduled_at ?? null,
          education: p.education ?? [],
          professions: p.professions ?? [],
        });
        setShowReadReceipts(preferences.show_read_receipts);
        // Eski sunucu bu alanı döndürmez; sunucudaki varsayılan da açık.
        setShowTyping(preferences.show_typing ?? true);
        setWhoCanMessage(preferences.who_can_message);
        // Sunucu bu alanı henüz döndürmüyorsa (eski dağıtım) varsayılana düş;
        // aksi hâlde hiçbir seçenek seçili görünmez.
        setWhoCanCall(preferences.who_can_call ?? 'everyone');
        setKeepArchived(preferences.keep_archived);
        setWebVisibility(webVisibilityOf(preferences));
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [token, myId, lang]);

  useEffect(() => {
    load();
  }, [load]);

  const displayLangName =
    DISPLAY_LANGUAGES.find((l) => l.code === lang)?.name ?? lang;

  function pickLanguage(code: string) {
    setLang(code); // context: hem kaydeder hem tüm arayüzü günceller
    setLangModal(false);
    setLangQuery('');
  }

  async function pickCurrency(code: string) {
    if (savingCurrency) return;
    setCurrencyModal(false);
    const prevCurrency = profile?.currency;
    setProfile((prev) => prev ? { ...prev, currency: code } : prev);
    if (!profile?.name) return;
    setSavingCurrency(true);
    try {
      const updated = await api.saveProfile(token, profile.name, profile.surname ?? undefined, undefined, undefined, undefined, undefined, code);
      setProfile((prev) => prev ? { ...prev, currency: updated.currency } : prev);
    } catch (e: any) {
      setProfile((prev) => prev ? { ...prev, currency: prevCurrency ?? 'USD' } : prev);
      Alert.alert(t('common.error'), e?.message || t('profile.saveFailed'));
    } finally {
      setSavingCurrency(false);
    }
  }

  function openUsernameModal() {
    setUsernameInput(profile?.username ?? '');
    setUsernameCheck('idle');
    setUsernameModal(true);
  }

  async function saveUsername() {
    const uname = usernameInput.trim();
    const unameChanged = uname.toLowerCase() !== (profile?.username ?? '').toLowerCase();
    if (!unameChanged || uname === '') { setUsernameModal(false); return; }
    if (!USERNAME_RE.test(uname) || usernameCheck === 'taken') {
      Alert.alert(
        t('common.error'),
        USERNAME_RE.test(uname) ? t('profile.usernameTaken') : t('profile.usernameInvalid')
      );
      return;
    }
    try {
      const r = await api.setUsername(token, uname);
      setProfile((prev) => prev ? { ...prev, username: r.username } : prev);
      setUsernameModal(false);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('profile.saveFailed'));
    }
  }

  async function confirmDeleteAccount() {
    // Para ön-kontrolü: bakiye/pending varsa uyar (marketplace kuralı)
    let moneyWarning = '';
    try {
      const w = await api.getWallet(token);
      const total = Number(w.balance) + Number(w.pending) + Number(w.escrow ?? 0);
      if (total > 0) {
        moneyWarning = '\n\n' + t('profile.deleteMoneyWarning', { amount: formatMoney(total, profile?.currency ?? 'USD', lang) });
      }
    } catch {}

    Alert.alert(t('profile.deleteTitle'), t('profile.deleteMessage') + moneyWarning, [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('profile.deleteConfirm'),
        style: 'destructive',
        onPress: async () => {
          try {
            await api.deleteAccount(token);
            load(); // banner görünsün
          } catch {
            Alert.alert(t('common.error'), t('profile.deleteFailed'));
          }
        },
      },
    ]);
  }

  async function cancelDeletion() {
    try {
      await api.restoreAccount(token);
      load();
    } catch {
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    }
  }

  async function exportData() {
    if (exportingData) return;
    setExportingData(true);
    try {
      const data = await api.exportData(token);
      await deliverAccountExport(data);
    } catch {
      Alert.alert(t('common.error'), t('profile.exportFailed'));
    } finally {
      setExportingData(false);
    }
  }

  const loadCallExceptions = useCallback(async (
    query: string,
    cursor?: string,
    replace = true,
  ) => {
    const requestId = ++callExceptionsRequestRef.current;
    if (replace) {
      setCallExceptionsLoading(true);
      setCallExceptionsFailed(false);
      setCallExceptionsLoadMoreFailed(false);
    } else {
      setCallExceptionsLoadingMore(true);
      setCallExceptionsLoadMoreFailed(false);
    }
    try {
      const page = await api.getCallExceptions(token, { query, cursor, limit: 30 });
      if (requestId !== callExceptionsRequestRef.current) return;
      setCallExceptions((current) => {
        const byId = new Map<number, CallException>();
        for (const item of replace ? [] : current) byId.set(item.id, item);
        for (const item of page.items) byId.set(item.id, item);
        return [...byId.values()];
      });
      setCallExceptionsCursor(page.next_cursor);
    } catch {
      if (requestId !== callExceptionsRequestRef.current) return;
      if (replace) setCallExceptionsFailed(true);
      else setCallExceptionsLoadMoreFailed(true);
    } finally {
      if (requestId === callExceptionsRequestRef.current) {
        setCallExceptionsLoading(false);
        setCallExceptionsLoadingMore(false);
      }
    }
  }, [token]);

  function openCallExceptions() {
    callExceptionsRequestRef.current += 1;
    setCallExceptionsQuery('');
    setCallExceptionsLoading(true);
    setCallExceptionsModal(true);
  }

  const loadMessageExceptions = useCallback(async (
    query: string,
    cursor?: string,
    replace = true,
  ) => {
    const requestId = ++messageExceptionsRequestRef.current;
    if (replace) {
      setMessageExceptionsLoading(true);
      setMessageExceptionsFailed(false);
      setMessageExceptionsLoadMoreFailed(false);
    } else {
      setMessageExceptionsLoadingMore(true);
      setMessageExceptionsLoadMoreFailed(false);
    }
    try {
      const page = await api.getMessageExceptions(token, { query, cursor, limit: 30 });
      if (requestId !== messageExceptionsRequestRef.current) return;
      setMessageExceptions((current) => {
        const byId = new Map<number, MessageException>();
        for (const item of replace ? [] : current) byId.set(item.id, item);
        for (const item of page.items) byId.set(item.id, item);
        return [...byId.values()];
      });
      setMessageExceptionsCursor(page.next_cursor);
    } catch {
      if (requestId !== messageExceptionsRequestRef.current) return;
      if (replace) setMessageExceptionsFailed(true);
      else setMessageExceptionsLoadMoreFailed(true);
    } finally {
      if (requestId === messageExceptionsRequestRef.current) {
        setMessageExceptionsLoading(false);
        setMessageExceptionsLoadingMore(false);
      }
    }
  }, [token]);

  function openMessageExceptions() {
    messageExceptionsRequestRef.current += 1;
    setMessageExceptionsQuery('');
    setMessageExceptionsLoading(true);
    setMessageExceptionsModal(true);
  }

  useEffect(() => {
    if (!callExceptionsModal) return;
    if (!callExceptionsQuery) {
      loadCallExceptions('');
      return;
    }
    const timer = setTimeout(() => {
      loadCallExceptions(callExceptionsQuery.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [callExceptionsModal, callExceptionsQuery, loadCallExceptions]);

  useEffect(() => {
    if (!messageExceptionsModal) return;
    if (!messageExceptionsQuery) {
      loadMessageExceptions('');
      return;
    }
    const timer = setTimeout(() => {
      loadMessageExceptions(messageExceptionsQuery.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [messageExceptionsModal, messageExceptionsQuery, loadMessageExceptions]);

  useEffect(() => {
    if (!exceptionReturnContext) return;
    if (exceptionReturnContext.kind === 'call') {
      setCallExceptionsQuery(exceptionReturnContext.query);
      setCallExceptionsLoading(true);
      setCallExceptionsModal(true);
    } else {
      setMessageExceptionsQuery(exceptionReturnContext.query);
      setMessageExceptionsLoading(true);
      setMessageExceptionsModal(true);
    }
    onExceptionReturnHandled?.();
  }, [exceptionReturnContext, onExceptionReturnHandled]);

  function openExceptionProfile(userId: number, kind: ExceptionListContext['kind']) {
    setCallExceptionsModal(false);
    setMessageExceptionsModal(false);
    onOpenUserProfile(userId, {
      kind,
      query: kind === 'call' ? callExceptionsQuery : messageExceptionsQuery,
    });
  }

  function openBlocked() {
    setBlockedModal(true);
    setBlockedLoading(true);
    api.getBlocked(token)
      .then(setBlockedUsers)
      .catch(() => setBlockedUsers([]))
      .finally(() => setBlockedLoading(false));
  }

  async function unblock(id: number) {
    try {
      await api.unblockUser(token, id);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== id));
    } catch {
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    }
  }

  function openAddEducation() {
    setAddEduQuery('');
    setAddEduResults([]);
    setAddEduUniversity(null);
    setAddEduStep('university');
    setAddEduModal(true);
  }

  async function pickEducationLevel(level: number) {
    if (!addEduUniversity || addEduSaving) return;
    setAddEduSaving(true);
    try {
      const edu = await api.addEducation(token, addEduUniversity.id, level, lang);
      setProfile((prev) => prev ? { ...prev, education: [...prev.education, edu] } : prev);
      setAddEduModal(false);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('profile.saveFailed'));
    } finally {
      setAddEduSaving(false);
    }
  }

  async function removeEducation(id: number) {
    const prevEducation = profile?.education ?? [];
    setProfile((prev) => prev ? { ...prev, education: prev.education.filter((e) => e.id !== id) } : prev);
    try {
      await api.deleteEducation(token, id);
    } catch {
      setProfile((prev) => prev ? { ...prev, education: prevEducation } : prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    }
  }

  function openAddProfession() {
    setAddProfQuery('');
    setAddProfResults([]);
    setAddProfModal(true);
  }

  async function pickProfession(item: Profession) {
    if (addProfSaving) return;
    setAddProfSaving(true);
    try {
      const prof = await api.addProfession(token, item.id, lang);
      setProfile((prev) => prev ? { ...prev, professions: [...prev.professions, prof] } : prev);
      setAddProfModal(false);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('profile.saveFailed'));
    } finally {
      setAddProfSaving(false);
    }
  }

  async function removeProfession(id: number) {
    const prevProfessions = profile?.professions ?? [];
    setProfile((prev) => prev ? { ...prev, professions: prev.professions.filter((p) => p.id !== id) } : prev);
    try {
      await api.deleteProfession(token, id);
    } catch {
      setProfile((prev) => prev ? { ...prev, professions: prevProfessions } : prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    }
  }

  useEffect(() => {
    setAddEduResults([]);
    setAddEduLoading(false);
    if (!addEduModal || addEduStep !== 'university' || addEduQuery.length < 2) return;
    let active = true;
    const timer = setTimeout(async () => {
      setAddEduLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/universities?q=${encodeURIComponent(addEduQuery)}&lang=${lang}`);
        if (!res.ok) return;
        const data = await res.json();
        if (active) setAddEduResults(Array.isArray(data) ? data : []);
      } catch { if (active) setAddEduResults([]); }
      finally { if (active) setAddEduLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [addEduQuery, addEduModal, addEduStep, lang]);

  useEffect(() => {
    if (!addProfModal) return;
    if (addProfQuery.length < 2) { setAddProfResults([]); return; }
    const timer = setTimeout(async () => {
      setAddProfLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/professions?q=${encodeURIComponent(addProfQuery)}&lang=${lang}`);
        setAddProfResults(await res.json());
      } catch { setAddProfResults([]); }
      finally { setAddProfLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addProfQuery, addProfModal, lang]);

  // #34 — kullanıcı adı müsaitlik kontrolü (debounce'lu, yalnız modal açıkken)
  useEffect(() => {
    if (!usernameModal) return;
    const v = usernameInput.trim();
    if (v === '' || v.toLowerCase() === (profile?.username ?? '').toLowerCase()) {
      setUsernameCheck('idle');
      return;
    }
    if (!USERNAME_RE.test(v)) { setUsernameCheck('invalid'); return; }
    setUsernameCheck('checking');
    const timer = setTimeout(async () => {
      try {
        const r = await api.checkUsername(v);
        setUsernameCheck(r.available ? 'available' : 'taken');
      } catch { setUsernameCheck('idle'); }
    }, 400);
    return () => clearTimeout(timer);
  }, [usernameInput, usernameModal, profile?.username]);

  function openProfileInfo() {
    setNameInput(profile?.name ?? '');
    setSurnameInput(profile?.surname ?? '');
    setBioInput(profile?.bio ?? '');
    setEmployerUsernameInput(profile?.employer?.username ?? '');
    setProfileInfoModal(true);
  }

  function openPriceEditor(kind: PriceKind, label: string, value: number) {
    // 7.6 / D-656 — alan, listede görünen biçimle açılır: Türkçede "50,00", "50.00" değil.
    setPriceInput(formatMoneyAmount(Number(value), lang));
    setPriceEditor({ kind, label });
  }

  async function saveProfileInfo() {
    if (!nameInput.trim()) return;
    setSaving(true);
    try {
      const updated = await api.saveProfile(
        token,
        nameInput.trim(),
        surnameInput.trim(),
        undefined,
        undefined,
        undefined,
        bioInput.trim(),
        undefined,
        employerUsernameInput.trim().replace(/^@/, '')
      );
      setProfile((prev) => prev ? {
        ...prev,
        name: updated.name,
        surname: updated.surname,
        bio: updated.bio,
        employer: updated.employer,
      } : prev);
      setProfileInfoModal(false);
    } catch (e: any) {
      const message = e?.message === 'employer_not_found'
        ? t('profile.employerNotFound')
        : e?.message === 'employer_self'
        ? t('profile.employerSelf')
        : e?.message || t('profile.saveFailed');
      Alert.alert(t('common.error'), message);
    } finally {
      setSaving(false);
    }
  }

  async function savePrice() {
    if (!profile || !priceEditor) return;
    const normalized = normalizeInputDigits(priceInput, lang).trim().replace(',', '.');
    const value = normalized === '' ? Number.NaN : Number(normalized);
    if (!Number.isFinite(value) || value < 0) {
      Alert.alert(t('common.error'), t('profile.saveFailed'));
      return;
    }
    setSaving(true);
    try {
      const updated = await api.saveProfile(
        token,
        profile.name,
        profile.surname ?? undefined,
        undefined,
        undefined,
        { [priceEditor.kind]: value }
      );
      setProfile((prev) => prev ? {
        ...prev,
        price_message: Number(updated.price_message),
        price_voice: Number(updated.price_voice),
        price_call: Number(updated.price_call),
        price_call_scheduled: Number(updated.price_call_scheduled ?? prev.price_call_scheduled),
        price_call_video: Number(updated.price_call_video ?? prev.price_call_video),
        price_time: Number(updated.price_time),
      } : prev);
      setPriceEditor(null);
    } catch (e: any) {
      Alert.alert(t('common.error'), e?.message || t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleReadReceipts(val: boolean) {
    if (!profile || savingReadReceipts) return;
    const prev = showReadReceipts;
    setShowReadReceipts(val);
    setSavingReadReceipts(true);
    try {
      const updated = await api.updateProfilePreferences(token, { show_read_receipts: val });
      setShowReadReceipts(updated.show_read_receipts);
      setProfile((current) => current
        ? { ...current, show_read_receipts: updated.show_read_receipts }
        : current);
    } catch {
      setShowReadReceipts(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingReadReceipts(false);
    }
  }

  async function toggleShowTyping(val: boolean) {
    if (!profile || savingShowTyping) return;
    const prev = showTyping;
    setShowTyping(val);
    setSavingShowTyping(true);
    try {
      const updated = await api.updateProfilePreferences(token, { show_typing: val });
      setShowTyping(updated.show_typing ?? val);
    } catch {
      setShowTyping(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingShowTyping(false);
    }
  }

  async function changeWhoCanMessage(val: string) {
    if (!profile || savingWhoCanMessage) return;
    const prev = whoCanMessage;
    setWhoCanMessage(val);
    setSavingWhoCanMessage(true);
    try {
      const updated = await api.updateProfilePreferences(token, {
        who_can_message: val as 'everyone' | 'contacts' | 'nobody',
      });
      setWhoCanMessage(updated.who_can_message);
      setProfile((current) => current
        ? { ...current, who_can_message: updated.who_can_message }
        : current);
    } catch {
      setWhoCanMessage(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingWhoCanMessage(false);
    }
  }

  // 9.4.7.5 — "kimler beni arayabilir". who_can_message ile aynı sözleşme:
  // iyimser güncelle, sunucu yanıtıyla uzlaştır, hata olursa eski değere dön.
  async function changeWhoCanCall(val: string) {
    if (!profile || savingWhoCanCall) return;
    const prev = whoCanCall;
    setWhoCanCall(val);
    setSavingWhoCanCall(true);
    try {
      const updated = await api.updateProfilePreferences(token, {
        who_can_call: val as 'everyone' | 'contacts' | 'nobody',
      });
      setWhoCanCall(updated.who_can_call ?? val);
    } catch {
      setWhoCanCall(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingWhoCanCall(false);
    }
  }

  async function changeWebVisibility(val: WebVisibility) {
    if (!profile || savingWebVisibility || val === webVisibility) return;
    const prev = webVisibility;
    setWebVisibility(val);
    setSavingWebVisibility(true);
    try {
      // İki alan birlikte yazılır; "Kimse"de arama tercihi de kapanır ki sayfa
      // yeniden açılınca kendiliğinden aramaya dönmesin.
      const updated = await api.updateProfilePreferences(token, {
        web_profile_visible: val !== 'nobody',
        search_visible: val === 'everyone',
      });
      setWebVisibility(webVisibilityOf(updated));
    } catch {
      setWebVisibility(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingWebVisibility(false);
    }
  }

  async function toggleKeepArchived(val: boolean) {
    if (!profile || savingKeepArchived) return;
    const prev = keepArchived;
    setKeepArchived(val);
    setSavingKeepArchived(true);
    try {
      const updated = await api.updateProfilePreferences(token, { keep_archived: val });
      setKeepArchived(updated.keep_archived);
      setProfile((current) => current
        ? { ...current, keep_archived: updated.keep_archived }
        : current);
    } catch {
      setKeepArchived(prev);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSavingKeepArchived(false);
    }
  }

  async function changePhoto() {
    if (photoSaving) return;
    setPhotoSaving(true);
    try {
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
      if (!result.canceled && result.assets[0]?.uri) {
        const { photo_url } = await api.uploadPhoto(token, result.assets[0].uri, result.assets[0].file);
        setProfile((prev) => prev ? { ...prev, photo_url } : prev);
      }
    } catch (error) {
      Alert.alert(t('common.error'), error instanceof ApiError && error.code === 'photo_upload_conflict'
        ? t('profile.photoChanged') : t('profile.photoFailed'));
    } finally {
      setPhotoSaving(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.black} />
      </View>
    );
  }

  if (failed || !profile) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{t('userProfile.failed')}</Text>
        <TouchableOpacity style={styles.retryBtn} onPress={load}>
          <Text style={styles.retryText}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: variant === 'settings' ? 0 : insets.top }]}>
      <ScrollView
        // 7.6 / D-648 — Ayarlar diğer alt sayfalar gibi ortak yüzen başlığı
        // kullanıyor; içerik onun altından geçiyor, tepede aynı yükseklik boş.
        contentContainerStyle={[
          styles.scrollContent,
          variant === 'settings' && { paddingTop: screenHeaderContentInset(insets.top) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled">
      {profile.deletion_scheduled_at && (
        <View style={styles.deletionBanner} testID="profile-deletion-banner">
          <Text style={styles.deletionBannerText} testID="profile-deletion-notice">
            {t('profile.deletionScheduled', {
              date: new Date(profile.deletion_scheduled_at).toLocaleDateString(lang),
            })}
          </Text>
          <TouchableOpacity
            style={styles.deletionCancelButton}
            testID="profile-deletion-cancel"
            accessibilityRole="button"
            accessibilityLabel={t('profile.deletionCancel')}
            onPress={cancelDeletion}>
            <Text style={styles.deletionCancel}>{t('profile.deletionCancel')}</Text>
          </TouchableOpacity>
        </View>
      )}
      {variant === 'profile' && (
        <View style={styles.headerRow} testID="profile-header-toolbar">
          <GlassControlButton
            testID="profile-settings-open"
            onPress={() => onSettings?.()}
            accessibilityLabel={t('profile.settings')}>
            <Ionicons name="settings-outline" size={22} color={colors.black} accessible={false} />
          </GlassControlButton>
          <NotificationBellButton onPress={onNotificationCenter} />
        </View>
      )}

      {variant === 'profile' && (<>
      <View style={styles.avatarWrap}>
        <TouchableOpacity onPress={() => setPhotoModal(true)} activeOpacity={0.7}
          testID="profile-photo-change" style={styles.photoChangeButton}
          accessibilityRole="button" accessibilityLabel={t('profile.photoChange')}>
          <View style={styles.avatarFrame} testID="profile-avatar-frame">
            <Avatar photoUrl={profile?.photo_url} name={profile?.name} size={96} style={styles.avatar} />
          </View>
        </TouchableOpacity>
      </View>

      <Text style={styles.name} testID="profile-display-name">{[profile?.name, profile?.surname].filter(Boolean).join(' ') || '—'}</Text>
      {profile?.bio && <Text style={styles.bio}>{profile.bio}</Text>}
      <ProfileSection separated header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('profile.prices'), lang)}</Text>}>
        {[
          { kind: 'message' as const, label: t('profile.priceMessage'), value: profile.price_message },
          { kind: 'voice' as const, label: t('profile.priceVoice'), value: profile.price_voice },
          { kind: 'call' as const, label: t('profile.priceCall'), value: profile.price_call },
          { kind: 'videoCall' as const, label: t('profile.priceVideoCall'), value: profile.price_call_video },
          { kind: 'scheduledCall' as const, label: t('profile.priceScheduledCall'), value: profile.price_call_scheduled },
          { kind: 'time' as const, label: t('profile.priceTime'), value: profile.price_time },
        ].map(({ kind, label, value }) => (
          <TouchableOpacity
            key={kind}
            testID={`profile-price-${kind}`}
            style={[styles.priceRow, stacked && styles.priceRowStacked]}
            onPress={() => openPriceEditor(kind, label, value)}
            accessibilityLabel={`${label}, ${formatMoney(Number(value) || 0, profile.currency, lang)}`}
            accessibilityRole="button">
            <Text style={[styles.priceLabel, styles.rowLabel, stacked && styles.rowLabelStacked]}>{kind === 'message' ? label : t(SHORT_PRICE_LABELS[kind])}</Text>
            <View style={[styles.priceValueRow, stacked && styles.priceValueRowStacked]}>
              <Text style={styles.priceValue}>{formatMoney(Number(value) || 0, profile.currency, lang)}</Text>
              {kind !== 'message' && <Text testID={`profile-price-${kind}-unit`} style={styles.priceUnit}>/ {t('calendar.minutesShort')}</Text>}
              <Text style={[styles.rowChevron, stacked && styles.rowChevronStacked]}>›</Text>
            </View>
          </TouchableOpacity>
        ))}
      </ProfileSection>

      <ProfileSection separated header={<View style={styles.sectionTitleRow}>
          <Text style={styles.pricesTitle}>
            {uppercaseForDisplayLanguage(t('profile.profession'), lang)}
          </Text>
          <TouchableOpacity
            style={styles.sectionAddControl}
            onPress={openAddProfession}
            accessibilityRole="button"
            accessibilityLabel={t('profile.professionAdd')}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="add-outline" size={18} color={profilePalette(colors).link} accessible={false} />
          </TouchableOpacity>
        </View>}>
        {profile.professions.length === 0 && (
          <Text style={[styles.settingHint, styles.emptySectionText]}>{t('profile.professionNone')}</Text>
        )}
        {profile.professions.map((prof) => (
          <View key={prof.id} style={[styles.priceRow, styles.removableRow]}>
            <Text style={[styles.priceLabel, styles.rowLabel]}>{prof.name}</Text>
            <TouchableOpacity
              style={styles.removeControl}
              onPress={() => removeProfession(prof.id)}
              accessibilityRole="button"
              accessibilityLabel={`${t('common.remove')}: ${prof.name}`}>
              <Ionicons name="close-outline" size={18} color={colors.gray600} accessible={false} />
            </TouchableOpacity>
          </View>
        ))}
      </ProfileSection>

      <ProfileSection separated header={<View style={styles.sectionTitleRow}>
          <Text style={styles.pricesTitle}>
            {uppercaseForDisplayLanguage(t('profile.education'), lang)}
          </Text>
          <TouchableOpacity
            style={styles.sectionAddControl}
            onPress={openAddEducation}
            accessibilityRole="button"
            accessibilityLabel={t('profile.educationAdd')}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="add-outline" size={18} color={profilePalette(colors).link} accessible={false} />
          </TouchableOpacity>
        </View>}>
        {profile.education.length === 0 && (
          <Text style={[styles.settingHint, styles.emptySectionText]}>{t('profile.educationNone')}</Text>
        )}
        {profile.education.map((edu) => (
          <View key={edu.id} style={[styles.educationRow, styles.removableRow]}>
            <View style={styles.educationDetails}>
              <Text style={styles.priceLabel}>{edu.university} · {t(`isced.${edu.isced_level}`)}</Text>
            </View>
            <TouchableOpacity
              style={styles.removeControl}
              onPress={() => removeEducation(edu.id)}
              accessibilityRole="button"
              accessibilityLabel={`${t('common.remove')}: ${edu.university}`}>
              <Ionicons name="close-outline" size={18} color={colors.gray600} accessible={false} />
            </TouchableOpacity>
          </View>
        ))}
      </ProfileSection>
      </>)}

      {variant === 'settings' && (<>
      <ProfileSection header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('profile.privacy'), lang)}</Text>}>
        <View style={styles.privacyToggleRow}>
          <View style={[styles.rowLabel, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('profile.readReceipts')}</Text>
            <InfoButton open={info.isOpen('readReceipts')} onPress={() => info.toggle('readReceipts')} subject={t('profile.readReceipts')} testID="profile-read-receipts-info" />
          </View>
          <View style={styles.privacyToggleTarget}>
            <Switch
              testID="profile-read-receipts-switch"
              style={styles.privacyToggle}
              hitSlop={12}
              value={showReadReceipts}
              accessibilityLabel={t('profile.readReceipts')}
              onValueChange={toggleReadReceipts}
              disabled={savingReadReceipts}
              trackColor={{ false: colors.gray200, true: profilePalette(colors).selectedSurface }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
        <InfoText open={info.isOpen('readReceipts')} text={t('profile.readReceiptsInfo')} style={styles.rowInfoText} testID="profile-read-receipts-info-text" />
        <View style={styles.privacyToggleRow}>
          <View style={[styles.rowLabel, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('profile.showTyping')}</Text>
            <InfoButton open={info.isOpen('showTyping')} onPress={() => info.toggle('showTyping')} subject={t('profile.showTyping')} testID="profile-show-typing-info" />
          </View>
          <View style={styles.privacyToggleTarget}>
            <Switch
              testID="profile-show-typing-switch"
              style={styles.privacyToggle}
              hitSlop={12}
              value={showTyping}
              accessibilityLabel={t('profile.showTyping')}
              onValueChange={toggleShowTyping}
              disabled={savingShowTyping}
              trackColor={{ false: colors.gray200, true: profilePalette(colors).selectedSurface }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
        <InfoText open={info.isOpen('showTyping')} text={t('profile.showTypingInfo')} style={styles.rowInfoText} testID="profile-show-typing-info-text" />
        <View style={styles.privacyToggleRow}>
          <View style={[styles.rowLabel, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('profile.keepArchived')}</Text>
            <InfoButton open={info.isOpen('keepArchived')} onPress={() => info.toggle('keepArchived')} subject={t('profile.keepArchived')} testID="profile-keep-archived-info" />
          </View>
          <View style={styles.privacyToggleTarget}>
            <Switch
              testID="profile-keep-archived-switch"
              style={styles.privacyToggle}
              hitSlop={12}
              value={keepArchived}
              accessibilityLabel={t('profile.keepArchived')}
              onValueChange={toggleKeepArchived}
              disabled={savingKeepArchived}
              trackColor={{ false: colors.gray200, true: profilePalette(colors).selectedSurface }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>
        <InfoText open={info.isOpen('keepArchived')} text={t('profile.keepArchivedInfo')} style={styles.rowInfoText} testID="profile-keep-archived-info-text" />
        <LabelWithInfo
          label={t('profile.whoCanMessage')}
          info={t('profile.whoCanMessageInfo')}
          labelStyle={styles.settingSubLabelText}
          style={styles.settingSubLabelRow}
          testID="profile-who-can-message"
        />
        <View testID="profile-who-can-message-options" style={styles.themeRow}>
          {([
            { key: 'everyone', label: t('profile.everyone') },
            { key: 'contacts', label: t('profile.contacts') },
            { key: 'nobody', label: t('profile.nobody') },
          ] as { key: string; label: string }[]).map(({ key, label }) => (
            <TouchableOpacity
              key={key}
              style={[styles.themeOption, whoCanMessage === key && styles.themeOptionActive]}
              accessibilityRole="radio"
              accessibilityState={{ checked: whoCanMessage === key, disabled: savingWhoCanMessage }}
              disabled={savingWhoCanMessage}
              onPress={() => changeWhoCanMessage(key)}>
              <Text
                style={[styles.themeOptionText, whoCanMessage === key && styles.themeOptionTextActive]}
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.72}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* 7.6 / D-656 — İstisnalar bir seçenek değil, bir liste açıyor; seçeneklerin
            arasında değil, altında kendi satırında. */}
        <TouchableOpacity
          testID="message-exceptions-option"
          style={styles.priceRow}
          accessibilityRole="button"
          accessibilityLabel={t('profile.callExceptions')}
          onPress={openMessageExceptions}>
          <Text
            testID="message-exceptions-option-label"
            style={[styles.priceLabel, styles.rowLabel]}
            numberOfLines={1}>
            {t('profile.callExceptions')}
          </Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        <LabelWithInfo
          label={t('profile.whoCanCall')}
          info={t('profile.whoCanCallInfo')}
          labelStyle={styles.settingSubLabelText}
          style={styles.settingSubLabelRow}
          testID="profile-who-can-call"
        />
        <View testID="profile-who-can-call-options" style={styles.themeRow}>
          {([
            { key: 'everyone', label: t('profile.everyone') },
            { key: 'contacts', label: t('profile.contacts') },
            { key: 'nobody', label: t('profile.nobody') },
          ] as { key: string; label: string }[]).map(({ key, label }) => (
            <TouchableOpacity
              key={key}
              style={[styles.themeOption, whoCanCall === key && styles.themeOptionActive]}
              accessibilityRole="radio"
              accessibilityState={{ checked: whoCanCall === key, disabled: savingWhoCanCall }}
              disabled={savingWhoCanCall}
              onPress={() => changeWhoCanCall(key)}>
              <Text
                style={[styles.themeOptionText, whoCanCall === key && styles.themeOptionTextActive]}
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.72}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* 7.6 / D-656 — İstisnalar bir seçenek değil, bir liste açıyor; seçeneklerin
            arasında değil, altında kendi satırında. */}
        <TouchableOpacity
          testID="call-exceptions-option"
          style={styles.priceRow}
          accessibilityRole="button"
          accessibilityLabel={t('profile.callExceptions')}
          onPress={openCallExceptions}>
          <Text
            testID="call-exceptions-option-label"
            style={[styles.priceLabel, styles.rowLabel]}
            numberOfLines={1}>
            {t('profile.callExceptions')}
          </Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        {/* 1.3.6–1.3.7 / D-657, D-659 — GDPR itiraz hakkı ve ötesi: üye sayfasını
            aramadan çıkarabilir ya da hiç açılmaz yapabilir. Batuhan: "Linki olan
            biri sayfayı yine açabiliyor. Bunun için de özellik gelmelidir." */}
        <LabelWithInfo
          label={t('profile.webVisibility')}
          info={t('profile.webVisibilityInfo')}
          labelStyle={styles.settingSubLabelText}
          style={styles.settingSubLabelRow}
          testID="profile-web-visibility"
        />
        <View testID="profile-web-visibility-options" style={styles.themeRow}>
          {([
            { key: 'everyone', label: t('profile.everyone') },
            { key: 'link', label: t('profile.webVisibilityLink') },
            { key: 'nobody', label: t('profile.nobody') },
          ] as { key: WebVisibility; label: string }[]).map(({ key, label }) => (
            <TouchableOpacity
              key={key}
              testID={`profile-web-visibility-${key}`}
              style={[styles.themeOption, webVisibility === key && styles.themeOptionActive]}
              accessibilityRole="radio"
              accessibilityState={{ checked: webVisibility === key, disabled: savingWebVisibility }}
              disabled={savingWebVisibility}
              onPress={() => changeWebVisibility(key)}>
              <Text
                style={[styles.themeOptionText, webVisibility === key && styles.themeOptionTextActive]}
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.72}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {Platform.OS !== 'web' && (<>
          {/* 9.32 / D-655 — linkin varsayılan görünümü. Web'de yok: tarayıcı
              başka bir sitenin sayfasını okuyamaz, web'den link hep açık adres gider. */}
          <LabelWithInfo
            label={t('message.linkViewLabel')}
            info={t('profile.linkViewInfo')}
            labelStyle={styles.settingSubLabelText}
            style={styles.settingSubLabelRow}
            testID="profile-link-view"
          />
          <View style={styles.themeRow}>
            {([
              { key: 'compact', label: t('message.linkViewCompact') },
              { key: 'plain', label: t('message.linkViewPlain') },
              { key: 'card', label: t('message.linkViewCard') },
            ] as { key: 'compact' | 'plain' | 'card'; label: string }[]).map(({ key, label }) => (
              <TouchableOpacity
                key={key}
                testID={`profile-link-view-${key}`}
                style={[styles.themeOption, linkViewDefault === key && styles.themeOptionActive]}
                accessibilityRole="radio"
                accessibilityState={{ checked: linkViewDefault === key }}
                onPress={() => changeLinkViewDefault(key)}>
                <Text
                  style={[styles.themeOptionText, linkViewDefault === key && styles.themeOptionTextActive]}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.72}>
                  {label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.privacyToggleRow}>
            <View style={[styles.rowLabel, styles.labelInfoRow]}>
              <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('profile.linkViewAsk')}</Text>
              <InfoButton open={info.isOpen('linkViewAsk')} onPress={() => info.toggle('linkViewAsk')} subject={t('profile.linkViewAsk')} testID="profile-link-view-ask-info" />
            </View>
            <View style={styles.privacyToggleTarget}>
              <Switch
                testID="profile-link-view-ask-switch"
                style={styles.privacyToggle}
                hitSlop={12}
                value={linkViewAsk}
                accessibilityLabel={t('profile.linkViewAsk')}
                onValueChange={changeLinkViewAsk}
                trackColor={{ false: colors.gray200, true: profilePalette(colors).selectedSurface }}
                thumbColor="#FFFFFF"
              />
            </View>
          </View>
          <InfoText open={info.isOpen('linkViewAsk')} text={t('profile.linkViewAskInfo')} style={styles.rowInfoText} />
        </>)}
        <TouchableOpacity testID="profile-blocked-row" style={styles.priceRow} onPress={openBlocked}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.blockedUsers')}</Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
      </ProfileSection>

      <ProfileSection header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('notifications.title'), lang)}</Text>}>
        <TouchableOpacity style={styles.priceRow} onPress={onNotifications}>
          <View style={[styles.settingText, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('notifications.preferences')}</Text>
            <InfoButton open={info.isOpen('notifications')} onPress={() => info.toggle('notifications')} subject={t('notifications.preferences')} testID="profile-notifications-info" />
          </View>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        <InfoText open={info.isOpen('notifications')} text={t('notifications.preferencesInfo')} style={styles.navInfoText} testID="profile-notifications-info-text" />
      </ProfileSection>

      <ProfileSection header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('dock.calendar'), lang)}</Text>}>
        <TouchableOpacity style={styles.priceRow} onPress={onCalendarConnections}>
          <View style={[styles.settingText, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>{t('calendar.connectionsTitle')}</Text>
            <InfoButton open={info.isOpen('calendar')} onPress={() => info.toggle('calendar')} subject={t('calendar.connectionsTitle')} testID="profile-calendar-info" />
          </View>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        <InfoText open={info.isOpen('calendar')} text={t('calendar.connectionsInfo')} style={styles.navInfoText} testID="profile-calendar-info-text" />
      </ProfileSection>

      <ProfileSection header={<Text style={styles.pricesTitle}>
          {uppercaseForDisplayLanguage(t('instagram.connectedAccounts'), lang)}
        </Text>}>
        <TouchableOpacity style={styles.priceRow} onPress={onInstagramConnections}>
          <View style={[styles.settingText, styles.labelInfoRow]}>
            <Text style={[styles.priceLabel, styles.labelInfoText]}>Instagram</Text>
            <InfoButton open={info.isOpen('instagram')} onPress={() => info.toggle('instagram')} subject={'Instagram'} testID="profile-instagram-info" />
          </View>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        <InfoText open={info.isOpen('instagram')} text={t('instagram.connectInfo')} style={styles.navInfoText} testID="profile-instagram-info-text" />
      </ProfileSection>

      <ProfileSection header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('profile.appearance'), lang)}</Text>}>
        <View style={styles.themeRow}>
          {([
            { key: 'light', label: t('profile.light') },
            { key: 'dark', label: t('profile.dark') },
            { key: 'system', label: t('profile.system') },
          ] as { key: ThemeMode; label: string }[]).map(({ key, label }) => (
            <TouchableOpacity
              key={key}
              style={[styles.themeOption, mode === key && styles.themeOptionActive]}
              accessibilityRole="radio"
              accessibilityState={{ checked: mode === key }}
              onPress={() => setMode(key)}>
              <Text
                style={[styles.themeOptionText, mode === key && styles.themeOptionTextActive]}
                numberOfLines={2}>
                {label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={styles.priceRow}
          testID="profile-language-row"
          onPress={() => { setLangQuery(''); setLangModal(true); }}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.displayLanguage')}</Text>
          <View style={styles.langValueRow}>
            <View style={styles.langBadge}><Text style={styles.langBadgeText}>{flagForLang(lang)}</Text></View>
            <Text style={styles.langValue}> {displayLangName}</Text>
            <Text style={styles.rowChevron}>›</Text>
          </View>
        </TouchableOpacity>
      </ProfileSection>

      <ProfileSection separated testID="profile-account-section" header={<Text style={styles.pricesTitle}>{uppercaseForDisplayLanguage(t('profile.account'), lang)}</Text>}>
        <TouchableOpacity testID="profile-information-row" style={styles.priceRow}
          accessibilityRole="button" onPress={openProfileInfo}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.information')}</Text>
          <Text style={styles.rowChevron} accessible={false}>›</Text>
        </TouchableOpacity>
        <TouchableOpacity testID="profile-sign-in-methods-row" style={styles.priceRow}
          accessibilityRole="button" onPress={() => setSignInMethodsModal(true)}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('signInMethods.title')}</Text>
          <Text style={styles.rowChevron} accessible={false}>›</Text>
        </TouchableOpacity>
        <TouchableOpacity testID="profile-username-row" style={styles.priceRow} onPress={openUsernameModal}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.username')}</Text>
          <View style={styles.langValueRow}>
            <Text style={styles.langValue}>{profile?.username ? `@${profile.username}` : t('profile.usernameAdd')}</Text>
            <Text style={styles.rowChevron}>›</Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity testID="profile-currency-row" style={styles.priceRow}
          accessibilityRole="button" accessibilityState={{ disabled: savingCurrency }}
          disabled={savingCurrency} onPress={() => setCurrencyModal(true)}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.currency')}</Text>
          <View style={styles.langValueRow}>
            <Text style={styles.langValue}>{profile?.currency}</Text>
            <Text style={styles.rowChevron} accessible={false}>›</Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.priceRow}
          testID="profile-account-data-row"
          onPress={() => setAccountModal(true)}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.accountData')}</Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
      </ProfileSection>

      {/* 7.6 / D-656 — Cüzdan ve Sohbet yedeği ayrı lacivert kartlar değil,
          diğer bütün ayarlar gibi bir bölümün satırları. */}
      <ProfileSection testID="profile-money-data-section" separated>
        <TouchableOpacity testID="profile-wallet-row" style={styles.priceRow}
          accessibilityRole="button" onPress={onWallet}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.wallet')}</Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
        {/* 9.34 / D-600 — şifreli geçmişin tek geri dönüş yolu: kurtarma kodu. */}
        <TouchableOpacity testID="profile-backup-row" style={styles.priceRow}
          accessibilityRole="button" onPress={() => setBackupSheet(true)}>
          <Text style={[styles.priceLabel, styles.rowLabel]}>{t('profile.chatBackup')}</Text>
          <Text style={styles.rowChevron}>›</Text>
        </TouchableOpacity>
      </ProfileSection>

      {/* Çıkış düğmeye benzemeyen düz bir yazıydı; artık kendi bölümünde kırmızı
          bir satır (D-656). Engelle ve rezervasyon iptali de aynı satırı kullanır (D-668). */}
      <SectionActionRow testID="profile-logout" label={t('profile.logout')} onPress={onLogout} />
      </>)}
      </ScrollView>
      {/* 7.6 / D-648 — ortadaki özel başlık yerine bütün alt sayfaların başlığı. */}
      {variant === 'settings' && (
        <PageHeader title={t('profile.settings')} onBack={() => onBack?.()} floating backTestID="settings-back" />
      )}

      <ChatBackupSheet
        visible={backupSheet}
        token={token}
        onClose={() => setBackupSheet(false)}
      />

      <Modal visible={photoModal} animationType="slide" onRequestClose={() => setPhotoModal(false)}>
        {photoModal && <View style={styles.modal}>
          <PageHeader title={[profile?.name, profile?.surname].filter(Boolean).join(' ')} onBack={() => setPhotoModal(false)} />
          <View style={styles.photoPreview}>
            {profile?.photo_url ? <Image testID="profile-photo-preview" source={{ uri: mediaUrl(profile.photo_url) }} style={styles.photoPreviewImage} resizeMode="contain" />
              : <Avatar name={profile?.name} size={96} />}
          </View>
          <View style={{ paddingHorizontal: spacing.md, paddingBottom: insets.bottom + spacing.md }}>
            <TouchableOpacity testID="profile-photo-edit" style={styles.button} accessibilityRole="button"
              accessibilityState={{ disabled: photoSaving }} disabled={photoSaving} onPress={changePhoto}>
              <Text style={styles.buttonText}>{photoSaving ? t('profile.saving') : t('profile.photoChange')}</Text>
            </TouchableOpacity>
          </View>
        </View>}
      </Modal>

      <Modal
        visible={profileInfoModal}
        animationType="slide"
        onRequestClose={() => { if (!saving) setProfileInfoModal(false); }}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('profile.information')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setProfileInfoModal(false)} disabled={saving}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView
            testID="profile-information-scroll"
            contentContainerStyle={[styles.profileInfoContent, { paddingBottom: insets.bottom + spacing.xl }]}
            scrollIndicatorInsets={{ bottom: insets.bottom }}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled">
            <Text style={styles.formLabel}>{t('profile.name')}</Text>
            <TextInput
              testID="profile-information-name"
              style={styles.formInput}
              value={nameInput}
              onChangeText={setNameInput}
              placeholder={t('profile.name')}
              placeholderTextColor={colors.gray400}
              autoFocus
            />
            <Text style={styles.formLabel}>{t('profile.surname')}</Text>
            <TextInput
              style={styles.formInput}
              value={surnameInput}
              onChangeText={setSurnameInput}
              placeholder={t('profile.surname')}
              placeholderTextColor={colors.gray400}
            />
            <Text style={styles.formLabel}>{t('profile.bio')}</Text>
            <TextInput
              style={[styles.formInput, styles.formBioInput]}
              value={bioInput}
              onChangeText={setBioInput}
              placeholder={t('profile.bio')}
              placeholderTextColor={colors.gray400}
              multiline
              maxLength={160}
            />
            <LabelWithInfo label={t('profile.employer')} info={t('profile.employerInfo')} labelStyle={styles.formLabel} testID="profile-employer" />
            <View testID="profile-employer-field" style={styles.employerInputRow}>
              <Text style={styles.employerAt}>@</Text>
              <TextInput
                style={styles.employerInput}
                value={employerUsernameInput}
                onChangeText={setEmployerUsernameInput}
                placeholder={t('profile.employerPlaceholder')}
                placeholderTextColor={colors.gray400}
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
            <TouchableOpacity
              testID="profile-information-save"
              style={[styles.button, styles.formSaveButton, (saving || !nameInput.trim()) && styles.buttonDisabled]}
              accessibilityRole="button"
              accessibilityState={{ disabled: saving || !nameInput.trim() }}
              onPress={saveProfileInfo}
              disabled={saving || !nameInput.trim()}>
              <Text style={styles.buttonText}>{saving ? t('profile.saving') : t('common.save')}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal
        visible={priceEditor !== null}
        animationType="slide"
        onRequestClose={() => { if (!saving) setPriceEditor(null); }}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{priceEditor?.label ?? t('profile.prices')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setPriceEditor(null)} disabled={saving}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView
            testID="profile-price-scroll"
            contentContainerStyle={[styles.priceEditorContent, { paddingBottom: insets.bottom + spacing.xl }]}
            scrollIndicatorInsets={{ bottom: insets.bottom }}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled">
            <View testID="profile-price-field" style={styles.priceEditorInputRow}>
              <Text style={styles.priceEditorCurrency}>{symbolFor(profile.currency)}</Text>
              <TextInput
                testID="profile-price-input"
                style={styles.priceEditorInput}
                value={priceInput}
                onChangeText={setPriceInput}
                keyboardType="decimal-pad"
                placeholder={formatMoneyAmount(0, lang)}
                placeholderTextColor={colors.gray400}
                autoFocus
                selectTextOnFocus
              />
            </View>
            <TouchableOpacity
              testID="profile-price-save"
              accessibilityRole="button"
              accessibilityState={{ disabled: saving }}
              style={[styles.button, styles.formSaveButton, saving && styles.buttonDisabled]}
              onPress={savePrice}
              disabled={saving}>
              <Text style={styles.buttonText}>{saving ? t('profile.saving') : t('common.save')}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={addEduModal} animationType="slide" onRequestClose={() => setAddEduModal(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {addEduStep === 'university' ? t('profile.educationAdd') : t('profile.educationLevel')}
            </Text>
            <TouchableOpacity
              style={styles.modalAction}
              accessibilityRole="button"
              onPress={() => addEduStep === 'level' ? setAddEduStep('university') : setAddEduModal(false)}>
              <Text style={styles.editBtn}>
                {addEduStep === 'level' ? t('common.back') : t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
          {addEduStep === 'university' ? (
            <>
              <TextInput
                style={styles.modalInput}
                placeholder={t('university.placeholder')}
                placeholderTextColor={colors.gray400}
                value={addEduQuery}
                onChangeText={setAddEduQuery}
                autoFocus
                autoCorrect={false}
              />
              {addEduLoading && <ActivityIndicator color={colors.black} style={{ marginTop: spacing.md }} />}
              <FlatList
                data={addEduResults}
                keyExtractor={(item) => String(item.id)}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.uniRow}
                    onPress={() => { setAddEduUniversity(item); setAddEduStep('level'); }}>
                    <Text style={styles.uniName}>{item.name}</Text>
                    <Text style={styles.uniCountry}>{localizedCountry(item.country_code, item.country, lang)}</Text>
                  </TouchableOpacity>
                )}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
              />
            </>
          ) : (
            <FlatList
              data={ISCED_LEVELS as unknown as number[]}
              keyExtractor={(level) => String(level)}
              contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
              renderItem={({ item: level }) => (
                <TouchableOpacity style={styles.uniRow} onPress={() => pickEducationLevel(level)} disabled={addEduSaving}>
                  <Text style={styles.uniName}>{t(`isced.${level}`)}</Text>
                </TouchableOpacity>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          )}
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={addProfModal} animationType="slide" onRequestClose={() => setAddProfModal(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('profile.professionAdd')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setAddProfModal(false)}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            style={styles.modalInput}
            placeholder={t('profession.placeholder')}
            placeholderTextColor={colors.gray400}
            value={addProfQuery}
            onChangeText={setAddProfQuery}
            autoFocus
            autoCorrect={false}
          />
          {addProfLoading && <ActivityIndicator color={colors.black} style={{ marginTop: spacing.md }} />}
          <FlatList
            data={addProfResults}
            keyExtractor={(item) => String(item.id)}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
            renderItem={({ item }) => (
              <TouchableOpacity style={styles.uniRow} onPress={() => pickProfession(item)} disabled={addProfSaving}>
                <Text style={styles.uniName}>{item.name}</Text>
                <Text style={styles.uniCountry}>{t(`isco.${iscoMajorOf(item.isco_group)}`)}</Text>
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={langModal} animationType="slide" onRequestClose={() => setLangModal(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('profile.displayLanguage')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setLangModal(false)}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            style={styles.modalInput}
            placeholder={t('profile.searchLanguage')}
            placeholderTextColor={colors.gray400}
            value={langQuery}
            onChangeText={setLangQuery}
            autoFocus
            autoCorrect={false}
          />
          <FlatList
            data={DISPLAY_LANGUAGES.filter((l) =>
              l.name.toLowerCase().includes(langQuery.toLowerCase()) ||
              l.code.toLowerCase().includes(langQuery.toLowerCase())
            )}
            keyExtractor={(item) => item.code}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
            renderItem={({ item }) => (
              <TouchableOpacity testID={`profile-language-option-${item.code}`} style={styles.langRow}
                accessibilityLabel={item.name}
                accessibilityRole="button" accessibilityState={{ selected: item.code === lang }}
                onPress={() => pickLanguage(item.code)}>
                <View style={styles.langBadge}><Text style={styles.langBadgeText}>{flagForLang(item.code)}</Text></View>
                <Text style={[styles.uniName, styles.selectionLabel, item.code === lang && styles.uniNameActive]}>
                  {item.name}
                </Text>
                {item.code === lang && <Ionicons name="checkmark-outline" size={18} color={profilePalette(colors).link} accessible={false} />}
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={blockedModal} animationType="slide" onRequestClose={() => setBlockedModal(false)}>
        <View style={styles.modal}>
          {/* 7.6 / D-648 — gezinme penceresi: sağ üstte "Geri" yazısı değil,
              bütün sayfalardaki gibi solda geri oku. */}
          <PageHeader title={t('profile.blockedUsers')} onBack={() => setBlockedModal(false)} backTestID="profile-blocked-back" />
          {blockedLoading ? (
            <ActivityIndicator color={colors.black} style={{ marginTop: spacing.lg }} />
          ) : (
            <FlatList
              data={blockedUsers}
              keyExtractor={(item) => String(item.id)}
              contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
              renderItem={({ item }) => (
                <View style={styles.blockedRow}>
                  <View style={styles.blockedIdentity}>
                    <Avatar photoUrl={item.photo_url} name={item.name} size={40} style={styles.blockedAvatar} />
                    <Text style={styles.blockedName}>{item.name}</Text>
                  </View>
                  <TouchableOpacity testID={`profile-unblock-${item.id}`} style={styles.unblockButton}
                    accessibilityRole="button" accessibilityLabel={`${t('profile.unblock')}: ${item.name}`}
                    onPress={() => unblock(item.id)}>
                    <Text style={styles.unblockText}>{t('profile.unblock')}</Text>
                  </TouchableOpacity>
                </View>
              )}
              ItemSeparatorComponent={() => <View style={styles.blockedSeparator} />}
              ListEmptyComponent={<Text style={styles.blockedEmpty}>{t('profile.blockedEmpty')}</Text>}
            />
          )}
        </View>
      </Modal>

      <ContactExceptionsModal
        visible={callExceptionsModal}
        loading={callExceptionsLoading}
        items={callExceptions}
        title={t('profile.callExceptions')}
        backLabel={t('common.back')}
        emptyText={t('profile.callExceptionsEmpty')}
        errorText={callExceptionsFailed ? t('profile.exceptionsLoadFailed') : null}
        retryLabel={t('common.retry')}
        searchText={callExceptionsQuery}
        searchPlaceholder={t('common.search')}
        searchEmptyText={t('inbox.searchEmpty')}
        hasMore={callExceptionsCursor !== null}
        loadingMore={callExceptionsLoadingMore}
        loadMoreErrorText={callExceptionsLoadMoreFailed ? t('profile.exceptionsLoadFailed') : null}
        loadMoreLabel={t('calendar.loadMore')}
        openProfileHint={t('profile.openProfileHint')}
        testID="call-exceptions-modal"
        topInset={insets.top}
        statusText={(item) => t(callExceptionStatusKey(item))}
        onRetry={() => loadCallExceptions(callExceptionsQuery.trim())}
        onSearchChange={(value) => {
          callExceptionsRequestRef.current += 1;
          setCallExceptionsQuery(value);
          setCallExceptionsLoading(true);
          setCallExceptionsFailed(false);
        }}
        onLoadMore={() => {
          if (callExceptionsCursor) {
            loadCallExceptions(callExceptionsQuery.trim(), callExceptionsCursor, false);
          }
        }}
        onItemPress={(item) => openExceptionProfile(item.id, 'call')}
        onClose={() => setCallExceptionsModal(false)}
      />

      <ContactExceptionsModal
        visible={messageExceptionsModal}
        loading={messageExceptionsLoading}
        items={messageExceptions}
        title={t('profile.callExceptions')}
        backLabel={t('common.back')}
        emptyText={t('profile.messageExceptionsEmpty')}
        errorText={messageExceptionsFailed ? t('profile.exceptionsLoadFailed') : null}
        retryLabel={t('common.retry')}
        searchText={messageExceptionsQuery}
        searchPlaceholder={t('common.search')}
        searchEmptyText={t('inbox.searchEmpty')}
        hasMore={messageExceptionsCursor !== null}
        loadingMore={messageExceptionsLoadingMore}
        loadMoreErrorText={messageExceptionsLoadMoreFailed ? t('profile.exceptionsLoadFailed') : null}
        loadMoreLabel={t('calendar.loadMore')}
        openProfileHint={t('profile.openProfileHint')}
        testID="message-exceptions-modal"
        topInset={insets.top}
        statusText={(item) => t(messageExceptionStatusKey(item))}
        onRetry={() => loadMessageExceptions(messageExceptionsQuery.trim())}
        onSearchChange={(value) => {
          messageExceptionsRequestRef.current += 1;
          setMessageExceptionsQuery(value);
          setMessageExceptionsLoading(true);
          setMessageExceptionsFailed(false);
        }}
        onLoadMore={() => {
          if (messageExceptionsCursor) {
            loadMessageExceptions(messageExceptionsQuery.trim(), messageExceptionsCursor, false);
          }
        }}
        onItemPress={(item) => openExceptionProfile(item.id, 'message')}
        onClose={() => setMessageExceptionsModal(false)}
      />

      <Modal visible={signInMethodsModal} animationType="slide" onRequestClose={() => setSignInMethodsModal(false)}>
        {signInMethodsModal && <SignInMethodsScreen token={token} onBack={() => setSignInMethodsModal(false)} />}
      </Modal>
      <Modal visible={accountModal} animationType="slide" onRequestClose={() => setAccountModal(false)}>
        <View style={styles.modal}>
          <PageHeader title={t('profile.accountData')} onBack={() => setAccountModal(false)} backTestID="profile-account-back" />
          <ScrollView testID="profile-account-scroll"
            contentContainerStyle={[styles.accountActions, { paddingBottom: insets.bottom + spacing.xl }]}
            scrollIndicatorInsets={{ bottom: insets.bottom }}>
            <TouchableOpacity
              style={styles.accountAction}
              testID="profile-export-data"
              accessibilityRole="button"
              accessibilityState={{ disabled: exportingData, busy: exportingData }}
              disabled={exportingData}
              onPress={exportData}>
              {exportingData ? (
                <ActivityIndicator color={colors.black} />
              ) : (
                <Text style={styles.exportText}>{t('profile.exportData')}</Text>
              )}
            </TouchableOpacity>
            {/* 7.6 / D-681 — çerçeveli kırmızı düğmeydi; Çıkış, Engelle ve iptal gibi
                kendi bölümünde kırmızı bir satır (D-668). Batuhan: "Uygundur". */}
            <View style={styles.deleteSection}>
              <SectionActionRow testID="profile-delete-account" label={t('profile.deleteAccount')} onPress={confirmDeleteAccount} />
            </View>
          </ScrollView>
        </View>
      </Modal>

      <Modal visible={usernameModal} animationType="slide" onRequestClose={() => setUsernameModal(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('profile.username')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setUsernameModal(false)}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView
            testID="profile-username-scroll"
            contentContainerStyle={{ paddingBottom: insets.bottom + spacing.xl }}
            scrollIndicatorInsets={{ bottom: insets.bottom }}
            keyboardDismissMode="on-drag"
            keyboardShouldPersistTaps="handled">
            <View testID="profile-username-field" style={styles.usernameInputRow}>
              <Text style={styles.usernameAt}>@</Text>
              <TextInput
                style={styles.usernameInput}
                value={usernameInput}
                onChangeText={(v) => setUsernameInput(v.replace(/[^a-zA-Z0-9_]/g, ''))}
                placeholder={t('profile.usernamePlaceholder')}
                placeholderTextColor={colors.gray400}
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
                maxLength={20}
              />
            </View>
            {usernameCheck !== 'idle' && (
              <Text
                style={[
                  styles.usernameStatus,
                  styles.usernameModalStatus,
                  usernameCheck === 'available' && styles.usernameOk,
                  (usernameCheck === 'taken' || usernameCheck === 'invalid') && styles.usernameBad,
                ]}>
                {usernameCheck === 'checking'
                  ? t('profile.usernameChecking')
                  : usernameCheck === 'available'
                  ? '✓ ' + t('profile.usernameAvailable')
                  : usernameCheck === 'taken'
                  ? t('profile.usernameTaken')
                  : t('profile.usernameInvalid')}
              </Text>
            )}
            <TouchableOpacity testID="profile-username-save" accessibilityRole="button"
              style={[styles.button, styles.formSaveButton, { marginHorizontal: spacing.lg }]} onPress={saveUsername}>
              <Text style={styles.buttonText}>{t('common.save')}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={currencyModal} animationType="slide" onRequestClose={() => setCurrencyModal(false)}>
        <View style={[styles.modal, { paddingTop: insets.top + spacing.md }]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('profile.currency')}</Text>
            <TouchableOpacity style={styles.modalAction} accessibilityRole="button" onPress={() => setCurrencyModal(false)}>
              <Text style={styles.editBtn}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <FlatList
            data={CURRENCIES}
            keyExtractor={(item) => item.code}
            contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + spacing.lg }}
            renderItem={({ item }) => (
              <TouchableOpacity
                testID={`profile-currency-option-${item.code}`}
                accessibilityLabel={`${item.symbol} ${item.code}`}
                style={styles.langRow}
                accessibilityRole="button"
                accessibilityState={{ selected: item.code === profile?.currency, disabled: savingCurrency }}
                disabled={savingCurrency}
                onPress={() => pickCurrency(item.code)}>
                <Text style={[styles.uniName, styles.selectionLabel, item.code === profile?.currency && styles.uniNameActive]}>
                  {item.symbol}  {item.code}
                </Text>
                {item.code === profile?.currency && <Ionicons name="checkmark-outline" size={18} color={profilePalette(colors).link} accessible={false} />}
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        </View>
      </Modal>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  scrollContent: { paddingBottom: 130 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.white },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  headerRow: {
    flexDirection: 'row',
    // Profilde dişli solda, zil sağda; ayarlarda geri solda, başlık ortada.
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  editBtn: { fontSize: fontSize.md, color: colors.gray600 },
  avatarWrap: { alignItems: 'center', marginTop: spacing.xs },
  photoChangeButton: { alignItems: 'center', maxWidth: '100%', paddingHorizontal: spacing.md },
  avatarFrame: { padding: 4, borderWidth: 1, borderColor: colors.gray200, borderRadius: 54, backgroundColor: profilePalette(colors).surround },
  avatar: { width: 96, height: 96, borderRadius: 48 },
  photoPreview: { flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' },
  photoPreviewImage: { width: '100%', height: '100%' },
  name: {
    fontSize: fontSize.lg,
    fontWeight: '600',
    color: profilePalette(colors).heading,
    textAlign: 'center',
    marginTop: 12,
    paddingHorizontal: spacing.lg,
  },
  usernameInputRow: { ...profileFieldStyle(colors), marginHorizontal: spacing.lg, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  usernameAt: { fontSize: fontSize.md, color: colors.gray600 },
  usernameInput: {
    fontSize: fontSize.md,
    color: colors.black,
    flex: 1,
    minWidth: 0,
    padding: 0,
  },
  usernameStatus: { fontSize: fontSize.sm, color: colors.gray400, marginTop: spacing.xs },
  usernameOk: { color: '#2e7d32' },
  usernameBad: { color: '#e53935' },
  button: {
    backgroundColor: profilePalette(colors).selectedSurface,
    minHeight: 48,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonDisabled: { backgroundColor: colors.gray400 },
  buttonText: { fontSize: fontSize.md, fontWeight: '600', color: profilePalette(colors).selectedText, textAlign: 'center' },
  accountActions: { paddingHorizontal: spacing.lg, gap: spacing.md },
  accountAction: {
    ...profileFieldStyle(colors),
    alignItems: 'center',
    justifyContent: 'center',
  },
  exportText: { fontSize: fontSize.md, color: profilePalette(colors).heading, fontWeight: '600', textAlign: 'center' },
  // Bölüm kendi 16'lık kenar boşluğunu taşır; sayfanın 24'lük iç boşluğuna
  // binmesin, çerçeve "Verilerimi indir" düğmesinin kenarıyla hizalansın.
  deleteSection: { marginHorizontal: -16, marginTop: -spacing.md },
  usernameModalStatus: { textAlign: 'center', marginHorizontal: spacing.lg },
  deletionBanner: {
    backgroundColor: '#e53935',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    borderRadius: radius.control,
    padding: spacing.md,
    alignItems: 'stretch',
  },
  deletionBannerText: { color: '#fff', fontSize: fontSize.sm },
  deletionCancelButton: {
    alignSelf: 'flex-end',
    maxWidth: '100%',
    minHeight: 44,
    minWidth: 44,
    marginTop: spacing.sm,
    justifyContent: 'center',
  },
  deletionCancel: { color: '#fff', fontSize: fontSize.sm, fontWeight: '800', flexShrink: 1 },
  bio: {
    fontSize: fontSize.md,
    color: colors.gray600,
    textAlign: 'center',
    marginTop: spacing.md,
    paddingHorizontal: spacing.xl,
    lineHeight: 22,
  },

  employerInputRow: {
    ...profileFieldStyle(colors),
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  employerAt: {
    fontSize: fontSize.md,
    color: colors.gray600,
  },
  employerInput: {
    flex: 1,
    minWidth: 0,
    fontSize: fontSize.md,
    color: colors.black,
    padding: 0,
  },
  pricesTitle: {
    flexShrink: 1,
    fontSize: fontSize.sm,
    fontWeight: '600',
    color: profilePalette(colors).heading,
    letterSpacing: 0.5,
  },
  sectionTitleRow: {
    gap: spacing.sm,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Dolgusu yok, ve bu bilinçli.
  //
  // Eskiden dolgu `surround` idi — yani düğmenin üstünde durduğu bölüm
  // başlığının rengiyle BİREBİR aynıydı. Görünmeyen bir kutu çizmek, kutu
  // çizmemekten farksız; dolguyu yalnız iki yüzey yer değiştirdiğinde fark
  // ettik. Görünür olması gereken şey mavi işaretin kendisi, arkasındaki
  // kutu değil. Dokunma alanı 44 pt olarak duruyor ve `hitSlop` ile genişliyor.
  sectionAddControl: {
    flexShrink: 0,
    minWidth: 44,
    minHeight: 44,
    borderRadius: radius.control,
    alignItems: 'center',
    justifyContent: 'center',
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    minHeight: 48,
  },
  educationRow: {
    minHeight: 48,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  educationDetails: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.md,
  },
  priceLabel: { fontSize: fontSize.md, color: colors.black },
  privacyToggleRow: { flexDirection: 'row', alignItems: 'center', minHeight: 48 },
  privacyToggleTarget: { width: 56, height: 48, flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  privacyToggle: { transform: [{ scale: 0.85 }] },
  // 7.14 / D-654 — etiket ve ⓘ aynı satırda; açıklama satırın altında.
  labelInfoRow: { flexDirection: 'row', alignItems: 'center' },
  labelInfoText: { flexShrink: 1 },
  rowInfoText: { marginTop: 0, marginRight: 72, marginBottom: 8 },
  navInfoText: { marginTop: -4, marginRight: 24, marginBottom: 8 },
  settingSubLabelRow: { marginTop: spacing.md },
  settingSubLabelText: { fontSize: fontSize.md, color: colors.black },
  rowLabel: { flex: 1, minWidth: 0, marginRight: spacing.md },
  removableRow: { paddingVertical: 2 },
  removeControl: { width: 44, minHeight: 44, flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  priceValue: { flexShrink: 0, fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  priceUnit: { fontSize: fontSize.sm, color: colors.gray600, marginLeft: spacing.xs },
  priceValueRow: { flexShrink: 0, flexDirection: 'row', alignItems: 'center' },
  // D-504 — yığılmış hâl. Satır sütuna dönüyor; etiket kendi satırında tam
  // genişlik alıyor, böylece kelime içinde bölünmesi için sebep kalmıyor.
  priceRowStacked: { flexDirection: 'column', alignItems: 'stretch' },
  // `flex: 1` sütun düzeninde YÜKSEKLİĞİ paylaştırır, genişliği değil; etiket
  // gereksiz yere uzardı. Sağdaki boşluk da artık anlamsız, alta iniyor.
  rowLabelStacked: { flex: 0, minWidth: 0, marginRight: 0, marginBottom: spacing.xs },
  // Değer satırı tam genişlik; chevron `marginLeft: 'auto'` ile sondaki kenara
  // itiliyor, yani yığılmış hâlde de satırın sonunda duruyor ve dokunulabilirlik
  // işareti yerini değiştirmiyor.
  priceValueRowStacked: { flexShrink: 1, width: '100%' as const },
  rowChevronStacked: { marginLeft: 'auto' as const },
  rowChevron: { flexShrink: 0, width: 12, textAlign: 'center', fontSize: fontSize.md, color: colors.gray600, marginLeft: spacing.sm },
  langValue: { flexShrink: 1, fontSize: fontSize.md, color: colors.gray600, textAlign: 'right' },
  langValueRow: { maxWidth: '58%', flexShrink: 1, flexDirection: 'row', alignItems: 'center' },
  langBadge: {
    borderRadius: 6,
    backgroundColor: colors.gray100,
    minWidth: 28,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  langBadgeText: { fontSize: 15, textAlign: 'center' },
  settingHint: { fontSize: fontSize.sm, color: colors.gray600, marginTop: 2, lineHeight: 20 },
  emptySectionText: { minHeight: 48, marginTop: 0, paddingVertical: 12 },
  settingText: { flex: 1, minWidth: 0, marginRight: spacing.md },
  themeRow: { flexDirection: 'row', gap: spacing.sm, paddingVertical: spacing.sm },
  themeOption: {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: profilePalette(colors).optionBorder,
    backgroundColor: profilePalette(colors).optionSurface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  themeOptionActive: {
    backgroundColor: profilePalette(colors).selectedSurface,
    borderColor: profilePalette(colors).selectedSurface,
  },
  themeOptionText: { fontSize: 14, color: colors.gray600, textAlign: 'center' },
  themeOptionTextActive: { color: profilePalette(colors).selectedText, fontWeight: '600' },
  modal: { flex: 1, backgroundColor: colors.white },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
  },
  modalTitle: { flex: 1, minWidth: 0, marginRight: spacing.md, fontSize: fontSize.lg, fontWeight: '600', color: profilePalette(colors).heading },
  modalAction: { flexShrink: 0, minHeight: 44, minWidth: 44, justifyContent: 'center', alignItems: 'flex-end' },
  profileInfoContent: { paddingHorizontal: spacing.lg, paddingBottom: spacing.xl },
  formLabel: {
    fontSize: fontSize.sm,
    color: colors.gray600,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  formInput: {
    ...profileFieldStyle(colors),
    fontSize: fontSize.md,
    color: colors.black,
  },
  formBioInput: { minHeight: 96, textAlignVertical: 'top' },
  formSaveButton: { marginTop: spacing.xl },
  priceEditorContent: { paddingHorizontal: spacing.lg },
  priceEditorInputRow: {
    ...profileFieldStyle(colors),
    flexDirection: 'row',
    alignItems: 'center',
  },
  priceEditorCurrency: { fontSize: fontSize.xl, color: colors.gray600, marginRight: spacing.sm },
  priceEditorInput: { flex: 1, minWidth: 0, padding: 0, fontSize: fontSize.xl, color: colors.black },
  modalInput: {
    ...profileFieldStyle(colors),
    fontSize: fontSize.md,
    color: colors.black,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  uniRow: { minHeight: 48, paddingVertical: 12, justifyContent: 'center' },
  langRow: { minHeight: 48, flexDirection: 'row', alignItems: 'center', paddingVertical: 12, gap: spacing.md },
  selectionLabel: { flex: 1, minWidth: 0 },
  uniName: { fontSize: fontSize.md, color: colors.black, fontWeight: '500' },
  uniNameActive: { fontWeight: '600', color: profilePalette(colors).heading },
  uniCountry: { fontSize: fontSize.sm, color: colors.gray600, marginTop: 2, lineHeight: 20 },
  separator: { height: 1, backgroundColor: colors.gray100 },
  blockedRow: { ...profileFieldStyle(colors), gap: spacing.sm },
  blockedIdentity: { flexDirection: 'row', alignItems: 'center', minWidth: 0 },
  blockedSeparator: { height: spacing.sm },
  blockedAvatar: { width: 40, height: 40, borderRadius: 20, marginRight: spacing.md },
  blockedName: { flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.black, fontWeight: '500' },
  unblockButton: { ...profileFieldStyle(colors), alignSelf: 'flex-end', maxWidth: '100%', justifyContent: 'center', alignItems: 'center' },
  unblockText: { fontSize: fontSize.md, color: profilePalette(colors).heading, fontWeight: '600', textAlign: 'center' },
  blockedEmpty: { fontSize: fontSize.md, color: colors.gray600, textAlign: 'center', paddingTop: spacing.xl, lineHeight: 22 },
});
